<script>
export default {
  props: {
    route: {
      type: [String, Object],
      default: null,
    },
    variant: {
      type: String,
      default: '',
    },
    icon: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: '',
    },
  },
  computed: {
    tagName() {
      return this.route ? "a" : "button";
    },
    buttonClassList() {
      return [this.variant, { icon: this.icon }, this.size];
    },
    buttonDisabled() {
      return this.route ? null : this.disabled;
    },
    buttonListeners() {
      if (!this.route) {
        return this.$listeners;
      }

      return {
        ...this.$listeners,
        click: this.handleLinkClick
      };
    },
    href() {
      if (!this.route) {
        return "#";
      }

      const route = this.$router.resolve(this.route);

      return route.href;
    }
  },
  methods: {
    handleLinkClick(event) {
      event.preventDefault();

      this.$emit("click");

      this.$router.push(this.route);
    }
  },
  render(createElement) {
    const data = {
      attrs: {},
      class: ["sc-button", this.buttonClassList],
      on: this.buttonListeners
    };

    if (this.route) {
      data.attrs.href = this.href;
    } else {
      data.attrs.disabled = this.buttonDisabled;
    }

    return createElement(this.tagName, data, this.$slots.default);
  }
};
</script>

<style lang="scss">
@import "~@/lib/scss/variables";

.sc-button {
  display: inline-block;

  border-radius: $border-radius;
  border: $border;
  min-width: 120px;
  padding: 8px $space;

  font-size: 1rem;
  font-weight: bold;
  text-align: center;
  line-height: 1.29;

  cursor: pointer;
  background: white;

  &.sm {
    font-size: 0.8rem;
    padding: 6px 12px;
    min-width: 80px;
  }

  &:hover:not(:disabled) {
    border-color: $primary-color;

    text-decoration: none;

    background: #eee;
  }

  &:focus:not(:disabled) {
    outline: 0;

    border-color: $primary-color;
  }
}

.sc-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.sc-button.clean {
  border: 0;
  border-color: transparent;
}

.sc-button.icon {
  min-width: auto;

  border-radius: 50%;

  width: 40px;
  height: 40px;
  padding: 0;
}

$variants: ("primary" $primary-color $primary-dark-color),
  ("secondary" $secondary-color $secondary-dark-color),
  ("warning" $warning-color $warning-dark-color),
  ("success" $success-color $success-dark-color),
  ("danger" $danger-color $danger-dark-color),
  ("amber" $amber-color $amber-dark-color),
  ("orange-cc" $orange-cc-color $orange-cc-dark-color),
  ("white-orange" $white $orange-cc-dark-color),
  ("white" $white $white-dark-color);

@each $variant in $variants {
  $name: nth($variant, 1);
  $color: nth($variant, 2);
  $dark-color: nth($variant, 3);

  .sc-button.#{$name} {
    border-radius: $border-radius;
    background: $color;
    box-shadow: $shadow-1dp;

    $text-color: $white;
    $border-color-default: $color;
    $border-color-hover: $dark-color;
    $border-color-focus: $color;

    @if $name == "white" {
      $text-color: $dark;
      $border-color-default: $warm-grey;
      $border-color-hover: $warm-grey;
      $border-color-focus: $warm-grey;
    }

    color: $text-color;
    border-color: $border-color-default;

    &:hover:not(:disabled) {
      background: $dark-color;
      border-color: $border-color-hover;
    }

    &:focus:not(:disabled) {
      border-color: $border-color-focus;
    }
  }

  .sc-button.#{$name}:disabled {
    background: desaturate($color, 50);
    border-color: desaturate($color, 50);
  }
}
</style>
